import React, { useEffect, useMemo } from "react";
import { AudioPlayer } from "./audio-player";
import './styles.scss';
import { ScrollModal } from "../../shared/scroll-modal";
import { ScrollModalApi } from "../../shared/scroll-modal/view";

import SpotipitanContent from "../spotipitan-content/spotipitan-content";
import { useLocation, useNavigate } from "react-router-dom";
import AppOptionsModal from "./app-options-modal/app-options-modal";
import classNames from "classnames";
import { Spotip17anFunctions } from "../../../utils/api";
import { filterList } from "../../../utils/utils";
import { Song } from "./types";
import useUserStore from "../../../store/UserState";
import appLogo from "../../../images/logo.png"
import { NavDock } from "./nav-dock/nav-dock";
import { auth } from "../../../firebase";
import useSongStore from "./store/SongState";
import toast from "react-hot-toast";
import { DEFAULT_USER_PHOTO } from "../../../utils/constants";
import { useScreenSize } from "../../../hooks";
import DesktopPlaylistsSidebar from "../spotipitan-content/desktop-playlists-sidebar/desktop-playlists-sidebar";
import DesktopRightSidebar from "../spotipitan-content/desktop-right-sidebar/desktop-right-sidebar";
import DiscordVoiceModal from "./discord-modal/discord-modal";



const SpotipitanView = () => {

    const navigate = useNavigate();

    const headerDiv = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const appOptionsModal = React.useRef<ScrollModalApi>(null);
    const lastScrollTop = React.useRef(0);
    const headerScroll = React.useRef(0);
    const [scrollbarWidth, setScrollbarWidth] = React.useState(0);
    const [searchOpen, setSearchOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState<string>("");
    const [headerHeight, setHeaderHeight] = React.useState(70);
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const [suggests, setSuggests] = React.useState<string[]>([]);
    const [songSuggests, setSongSuggests] = React.useState<Song[]>([]);
    const focusedSuggest = React.useRef(-1);
    const [focusSuggestions, setFocusSuggestions] = React.useState(-1);
    const { offlineMode } = useUserStore(state => state.state);
    const { state: songState } = useSongStore(state => state);
    const { isBotMode } = songState;
    const user = useUserStore(state => state.state.user);
    const { width } = useScreenSize();

    const location = useLocation();

    const discordUser = useUserStore(state => state.state.discordUser);
    const discordVoiceModalRef = React.useRef<ScrollModalApi>(null);

    const handleOpenDiscordVoiceModal = (e?: React.MouseEvent<HTMLElement>) => {
        discordVoiceModalRef.current?.open({
            x: e?.clientX,
            y: e?.clientY
        });
    };

    const resetHeader = () => {
        headerDiv.current!.style.transform = ``;
        headerDiv.current!.style.height = `${headerHeight}px`;
    }

    const filteredSuggests = useMemo(() => {

        const auxList = suggests.map(x => ({ suggest: x, type: 'hist' }));

        auxList.push(...songSuggests.map(x => ({ suggest: x.name, type: 'song' })));

        return filterList(auxList, searchQuery, (suggest) => suggest.suggest);
    }, [suggests, songSuggests, searchQuery]);

    useEffect(() => {
        if (!contentRef.current) return;

        const handleHeightChange = () => {
            if (contentRef.current) {
                contentRef.current.style.height = `${window.innerHeight}px`;
                // get contentRef scroll width
                setScrollbarWidth(contentRef.current.offsetWidth - contentRef.current.clientWidth);
            }
        }

        const handleScroll = (e: any) => {

            if (e.target.scrollTop <= 0) {
                resetHeader();
                return;
            }

            const delta = e.target.scrollTop - lastScrollTop.current;

            lastScrollTop.current = e.target.scrollTop;
            headerScroll.current = Math.min(headerHeight, Math.max(0, headerScroll.current + (delta)));
            const opacity = (((headerScroll.current / 2) * -1) + headerHeight) / headerHeight;

            const translateY = (headerScroll.current / 2) * -1;

            headerDiv.current!.style.transform = translateY !== 0 ? `translateY(${translateY}px)` : "";

            headerDiv.current!.style.height = `${opacity * headerHeight}px`;
        }

        handleHeightChange();

        window.addEventListener('resize', handleHeightChange);
        contentRef.current.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('resize', handleHeightChange);
            if (contentRef.current) contentRef.current.removeEventListener('scroll', handleScroll);
        }
    }, [contentRef.current, headerHeight]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!searchInputRef.current) return;

            if (e.key === "f" && e.ctrlKey) {
                e.stopPropagation();
                e.preventDefault();
                focusedSuggest.current = -1;
                setFocusSuggestions(focusedSuggest.current);
                handleClickSearchIcon(true);
            }
            if (e.key === "Escape") {
                e.stopPropagation();
                e.preventDefault();
                focusedSuggest.current = -1;
                setFocusSuggestions(focusedSuggest.current);
                searchInputRef.current.blur();
                handleClickSearchIcon(false);
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [searchInputRef.current, showSuggestions, filteredSuggests, songSuggests]);

    useEffect(() => {
        setSearchOpen(false);
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        resetHeader();
    }, [location]);

    useEffect(() => {
        if (searchOpen) {
            focusedSuggest.current = -1;
            setFocusSuggestions(focusedSuggest.current);
            setHeaderHeight(148);
        } else {
            setHeaderHeight(70);
        }
    }, [searchOpen]);

    const handleOpenAppOptions = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        appOptionsModal.current?.open({ x: e.clientX, y: e.clientY });
    }

    const handleNavigate = (route: string) => {
        navigate(route);
        appOptionsModal.current?.close();
    }

    const handleClickSearchIcon = (value: boolean) => {
        setSearchOpen(value);
        document.querySelector('.spotipitan__search__suggest__content')!.scrollTo(0, 0);
        if (value) {
            document.getElementById("songSearcher")?.focus();
        }
    }

    const handleKeyDownSearchInput = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSendQuery();
        } else if (e.key === "Backspace") {
            focusedSuggest.current = -1;
            setFocusSuggestions(focusedSuggest.current);
        } /*else if ((e.key === "ArrowDown" || e.key === "ArrowUp") && showSuggestions) {

            e.stopPropagation();
            e.preventDefault();

            if(e.key === "ArrowDown") {
                focusedSuggest.current = Math.min(focusedSuggest.current + 1, filteredSuggests.length - 1);
            } else {
                focusedSuggest.current = Math.max(focusedSuggest.current - 1, 0);
            }

            setFocusSuggestions(focusedSuggest.current);
            setSearchQuery(filteredSuggests[focusedSuggest.current].suggest);

            // scroll to focused element
            const element = document.getElementById(`suggest-${focusedSuggest.current}`);
            if(element) {
                element.scrollIntoView({block: "nearest"});
            }
        }*/
    }

    const handleSendQuery = (text?: string) => {
        if (!text) {
            text = searchQuery;
        }
        if (!text) {
            return;
        }
        document.getElementById("songSearcher")?.blur();
        navigate(`/search?q=${text}`);
    }

    const handleCleanQuery = () => {
        handleChangeSearchQuery({ target: { value: "" } } as any);
        document.getElementById("songSearcher")?.focus();
    }

    const handleFocusSearchInput = () => {
        Spotip17anFunctions.use('getSearchHistory').call({ query: searchInputRef.current?.value ?? "" }).then((res) => {
            setSuggests(res);
            setShowSuggestions(true);
        }).catch(() => {
            setShowSuggestions(false);
        });
        resetHeader();
    }

    const handleChangeSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        document.querySelector('.spotipitan__search__suggest__content')!.scrollTo(0, 0);
        if (e.target.value.length < 3) {
            setSongSuggests([]);
        } else {
            Spotip17anFunctions.use('fetchSongsByKeywords').call({ keywords: e.target.value }).then((res: Song[]) => {
                setSongSuggests(res);
            });
        }
    }

    const handleClickUserIcon = () => {
        handleNavigate("my-user");
    }

    return (
        <div>
            <div ref={headerDiv} className="spotipitan__header" style={{
                right: scrollbarWidth,
                height: headerHeight,
            }}>
                <div className="container spotipitan__page-header">
                    <div className="spotipitan__page-header__left cursor-pointer" onClick={() => {
                        handleNavigate("");
                    }}>
                        <img id="img-page-header" alt="spotip17an" src={appLogo} height="50" />
                        <span className="spotipitan__page-header__left__title">Spotip17an</span>
                    </div>
                    <div className="spotipitan__page-header__right">
                        {offlineMode && <i onClick={handleOpenAppOptions} style={{
                            color: "white",
                        }} className="fa-solid fa-user"></i>}
                        {!offlineMode && (
                            <div className="d-flex align-items-center gap-3">
                                <div className="discord-header-icon-container">
                                    <i
                                        className={classNames("fa-brands fa-discord cursor-pointer discord-header-icon", {
                                            "discord-header-icon--active": isBotMode && discordUser
                                        }, {
                                            "discord-header-icon--disabled": !user?.uid
                                        })}
                                        onClick={handleOpenDiscordVoiceModal}
                                    ></i>
                                    {isBotMode && discordUser && (
                                        <span className="discord-active-badge"></span>
                                    )}
                                </div>
                                <img style={{
                                    width: "50px",
                                    height: "50px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                    cursor: "pointer"
                                }} src={auth.currentUser?.photoURL ?? DEFAULT_USER_PHOTO} onClick={handleClickUserIcon} className="fa-solid fa-user" />
                            </div>
                        )}
                    </div>
                </div>
                <div className={classNames("spotipitan__search", { "spotipitan__search__open": searchOpen || width > 1200 })}>
                    <div className="container">
                        <div className="form-floating mb-3">
                            <input type="text" className="form-control" id="songSearcher" placeholder="Song search"
                                name={"songSearcher"} autoComplete="off" spellCheck={false} autoCapitalize="off"
                                value={searchQuery} onChange={handleChangeSearchQuery}
                                ref={searchInputRef}
                                onKeyDown={handleKeyDownSearchInput}
                                onFocus={handleFocusSearchInput}
                                onBlur={() => {
                                    requestAnimationFrame(() => {
                                        setTimeout(() => {
                                            setShowSuggestions(false);
                                        }, 20);
                                    });
                                }} />
                            <label htmlFor="songSearcher">Busqueda de canciones</label>
                            <i className={classNames("fa-solid fa-xmark clean-query-icon", { "disabled": !searchQuery?.length })}
                                onClick={handleCleanQuery}></i>
                            <i className={classNames("fa-solid fa-paper-plane send-query-icon", { "disabled": !searchQuery?.length })}
                                onClick={() => handleSendQuery()}></i>
                        </div>
                    </div>
                </div>
                {width > 1200 && <div className="spotipitan-left-sidebar">
                    <DesktopPlaylistsSidebar />
                </div>}
                {width > 1200 && <div style={{
                    paddingTop: 10,
                }}>
                    <button
                        onClick={handleOpenAppOptions}
                        onContextMenu={handleOpenAppOptions}
                        className="navigate-library-button btn btn-link text-light" style={{
                            marginLeft: 0,
                            padding: width < 1450 ? 0 : "0 15px",
                            width: width < 1450 ? '50px' : '200px',
                            justifyContent: width < 1450 ? "center" : "left",
                            gap: 20
                        }}>
                        <i className="fa-solid fa-gear"></i>
                        {width >= 1450 && <span style={{
                            fontSize: "18px"
                        }}>Opciones</span>}
                    </button>
                </div>}

            </div>
            {width > 1200 && <div className="spotipitan-right-sidebar">
                <DesktopRightSidebar />
            </div>}
            <div
                className={classNames("spotipitan__search__suggest", { "spotipitan__search__suggest__open": showSuggestions })}>
                <div className="container spotipitan__search__suggest__content">
                    {
                        filteredSuggests.map((suggest, index) => {
                            return (<div key={suggest.type + "-" + index}
                                className={classNames("spotipitan__search__suggest__item cursor-pointer", {
                                    "spotipitan__search__suggest__item__focus": focusSuggestions === index
                                })}
                                id={"suggest-" + index}
                                onPointerDown={(e) => {
                                    if (e.pointerType === "mouse") {
                                        setSearchQuery(suggest.suggest);
                                        handleSendQuery(suggest.suggest);
                                    }
                                }}
                                onClick={() => {
                                    setSearchQuery(suggest.suggest);
                                    handleSendQuery(suggest.suggest);
                                }}>
                                {suggest.type === 'song' ?
                                    <i className="fa-solid fa-magnifying-glass"></i> :
                                    <i className="fa-solid fa-clock-rotate-left"></i>} {suggest.suggest}
                            </div>)
                        })
                    }
                </div>
            </div>
            <div ref={contentRef}
                onScroll={(e) => {
                    if (searchOpen)
                        searchInputRef.current?.blur();
                }}
                className={classNames("spotipitan__content", { "spotipitan__content__search-opened": searchOpen })}>
                <SpotipitanContent />
            </div>
            <AudioPlayer />
            <NavDock currentRoute={searchOpen ? "/search-open" : location.pathname} isOffline={offlineMode} handleOpenOptions={handleOpenAppOptions} handleHome={() => handleNavigate("")} handleOpenSearch={() => handleClickSearchIcon(!searchOpen)}
                handleOpenLibrary={() => handleNavigate("my-library")} />
            <AppOptionsModal handleNavigate={handleNavigate} ref={appOptionsModal} />
            <DiscordVoiceModal ref={discordVoiceModalRef} />
        </div>

    );
}

export default SpotipitanView;
